import {Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import sha256 from 'crypto-js/sha256';
import {Subscription} from 'rxjs';
import {map, tap} from 'rxjs/internal/operators';
import {APP_CONFIG} from 'src/app/config';
import {BoService} from 'src/app/services/bo-service/bo.service';
import {CommunicationService} from 'src/app/services/communication-service/communication.service';
import {NotificationsService} from 'src/app/services/notifications-services/notifications.service';
import {UserService} from 'src/app/services/user-service/user.service';
import {UserStorageService} from 'src/app/services/user-storage-service/user-storage.service';
import {MsalService} from "@azure/msal-angular";
import { LoginVerificationChallenge } from 'src/app/models/login-verification-model/login-verification-challenge.model';
import { LoginVerificationContact } from 'src/app/models/login-verification-model/login-verification-contact.model';

declare var google: any;

@Component({
  selector: 'app-sign-in-home-card',
  templateUrl: './signin-home-card.component.html',
  styleUrls: ['./signin-home-card.component.scss']
})
export class SignInHomeCardComponent implements OnInit, OnDestroy {

  @ViewChild('logo', {static: false}) logo: ElementRef;
  @ViewChild('form', {static: false}) form: ElementRef;

  basicDataForm = new FormGroup({
    email: new FormControl(null, Validators.required),
    password: new FormControl(null, Validators.required)
  });
  appConfig;
  public errorMessage: string;
  public logoHeight;
  public showLogo: boolean;
  public showCapsLockMessage: boolean;
  private signInSubscription: Subscription;

  private companies;
  private tmpContext;
  private googleIsInitialized = false;

  private googleClientId = '295683732969-ptbl4m6g6u9s4a1uruhlfktrpdo60270.apps.googleusercontent.com';

  constructor(private userService: UserService,
              private userStorageService: UserStorageService,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private boService: BoService,
              private notificationsService: NotificationsService,
              private communicationService: CommunicationService,
              private msalService: MsalService) {
    this.appConfig = APP_CONFIG;
  }

  ngOnInit() {
    this.errorMessage = null;
    if (this.activatedRoute.snapshot.queryParams['loginVerificationExpired']) {
      this.errorMessage = 'signin-page.snackbar messages.login verification expired';
    }
    this.resize();
    this.initGoogleLogin();
  }

  private initGoogleLogin() {
    const checkGoogle = setInterval(() => {
      if (typeof google !== 'undefined' && google.accounts) {
        clearInterval(checkGoogle);
        this.initializeGoogleIdentity();
        this.renderGoogleButton();
      }
    }, 100);
  }

  private initializeGoogleIdentity() {
    google.accounts.id.initialize({
      client_id: this.googleClientId,
      callback: (response: any) => this.handleGoogleLogin(response),
      auto_select: false,
      use_fedcm_for_prompt: false,
      context: 'signin',
      itp_support: true
    });
    this.googleIsInitialized = true;
  }

  private renderGoogleButton() {
    const container = document.getElementById('googleBtnSeparator');
    if (!container) {
      return;
    }

    container.innerHTML = '';
    google.accounts.id.renderButton(
      container,
      {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        shape: 'rectangular',
        text: 'signin_with',
        logo_alignment: 'left',
        width: 260
      }
    );
  }

  @HostListener('window:resize', ['$event'])
  public resize() {
    this.logoHeight = window.innerHeight * 0.25;
    window.innerHeight < 550 ? this.showLogo = false : this.showLogo = true;
  }

  public webLogin() {
    this.errorMessage = null;
    const validation = this.validateForm();
    if (validation === true) {
      this.userStorageService.deleteLoginVerificationChallenge();
      this.signInSubscription = this.userService.webLogin(this.userStorageService.getLang(), this.basicDataForm.get('email').value, this.userStorageService.getUDID(), this.hashPassword(this.basicDataForm.get('password').value)).subscribe(
        (res) => {
          if (this.isLoginVerificationRequiredResponse(res)) {
            this.handleLoginVerificationRequired(res['data']);
            return;
          }
          if (res && res['data']) {
            this.tmpContext = res['data']['context'];
            this.companies = res['data']['companies'];
            this.setAccountSelectorData();
            if (this.companies.length === 1) {
              this.selectCompany(this.companies[0].companyCode);
            } else if (this.companies.length > 1) {
              this.router.navigate(['/signin/account-selector']);
            } else {
              this.errorMessage = 'signin-page.snackbar messages.empty companies';
            }
          } else {
            this.errorMessage = 'signin-page.snackbar messages.wrong credentials';
          }
        },
        (error) => {
          if (this.isLoginVerificationRequiredError(error)) {
            this.handleLoginVerificationRequired(error['error']['data']);
            return;
          }
          this.errorMessage = 'signin-page.snackbar messages.wrong credentials';
        }
      );
    } else {
      this.errorMessage = validation;
    }
  }

  selectCompany(companyCode: string) {
    this.signInSubscription = this.userService.selectCompany(
      this.userStorageService.getTmpContext(),
      this.userStorageService.getLang(),
      this.userStorageService.getUDID(),
      companyCode).subscribe(
      (res) => {
        if (res) {
          this.setUserStorageData(res['data']);
          this.getVersion();
          this.router.navigate(['']);
          this.socketsConnect();
        }
      },
      (error) => {
        this.errorMessage = error;
      }
    );
  }

  public validateForm() {
    if (!this.basicDataForm.get('email').valid || !this.basicDataForm.get('password').valid) {
      return 'signin-page.snackbar messages.empty credentials';
    } else {
      return true;
    }
  }

  private hashPassword(password) {
    return sha256(password).toString().toUpperCase();
  }

  public toLowerCase(eRef) {
    eRef.value = eRef.value.toLowerCase();
  }

  public detectBloqMayus(e) {
    if (e.getModifierState('CapsLock')) {
      this.showCapsLockMessage = true;
    } else {
      this.showCapsLockMessage = false;
    }
  }

  private setAccountSelectorData() {
    this.userStorageService.setCompanyList(this.companies);
    this.userStorageService.setTmpContext(this.tmpContext);
  }

  private isLoginVerificationRequiredResponse(res) {
    return res && res['error'] === 'LOGIN_VERIFICATION_REQUIRED' && res['data'] && res['data']['challengeId'];
  }

  private isLoginVerificationRequiredError(error) {
    return error && error['error'] && error['error']['error'] === 'LOGIN_VERIFICATION_REQUIRED' && error['error']['data'] && error['error']['data']['challengeId'];
  }

  private handleLoginVerificationRequired(data) {
    const contacts = new Array<LoginVerificationContact>();
    if (data['contacts']) {
      data['contacts'].forEach(
        (contact) => {
          contacts.push(Object.assign(new LoginVerificationContact(), contact));
        }
      );
    }
    this.userStorageService.setLoginVerificationChallenge(new LoginVerificationChallenge(data['challengeId'], contacts));
    this.router.navigate(['/signin/login-verification-contact']);
  }

  private setUserStorageData(data) {
    this.userStorageService.setCompanyData(data['companyData']);
    this.userStorageService.setContext(data['context']);
    this.userStorageService.setMenuSectionList(data['menuOptions']);
    this.userStorageService.setUserData(data['userData']);
    this.userStorageService.setCenterList(data['centerList']);
  }

  private getVersion() {
    this.boService.getVersion().pipe(
      tap(o => {
        if (this.userStorageService.setBOVersion(o)) {
          if (this.boService.newVersionAvailable()) {
            this.notificationsService.notificate('fas fa-sync-alt', 'New version available', false, 'window.location.reload(true);');
          }
        }
      })
    );
  }

  private socketsConnect() {
    this.communicationService.getSocketChannels().pipe(
      map(socketChannels => this.userStorageService.setSocketChannels(socketChannels['channelList'])),
      map(socketChannels => this.userStorageService.getSocketChannels().filter(sc => sc.FlagCompany === true)[0]),
      map(socketChannel => this.communicationService.listenCommunications(socketChannel))
    ).subscribe();
  }

  public loginWithMicrosoft() {
    this.errorMessage = null;

    this.msalService.loginPopup({
      scopes: ['openid', 'profile', 'email'],
      prompt: 'select_account'
    }).subscribe(
      (res) => {
        const idToken = res.idToken;
        this.ssoMicrosoftBackendLogin(idToken);
      },
      (error) => {
        console.error('Error en login de Microsoft', error);
        this.errorMessage = 'signin-page.snackbar messages.sso microsoft error';
      }
    );
  }

  private ssoMicrosoftBackendLogin(token: string) {
    this.signInSubscription = this.userService.ssoMicrosoftLogin(
      this.userStorageService.getLang(),
      this.userStorageService.getUDID(),
      token
    ).subscribe(
      (res) => {
        if (res) {
          this.tmpContext = res['data']['context'];
          this.companies = res['data']['companies'];
          this.setAccountSelectorData();
          if (this.companies.length === 1) {
            this.selectCompany(this.companies[0].companyCode);
          } else if (this.companies.length > 1) {
            this.router.navigate(['/signin/account-selector']);
          } else {
            this.errorMessage = 'signin-page.snackbar messages.empty companies';
          }
        }
      },
      (error) => {
        this.errorMessage = 'signin-page.snackbar messages.wrong credentials';
      }
    );
  }

  public loginWithGoogle() {
    this.errorMessage = null;
    if (typeof google === 'undefined' || !google.accounts || !google.accounts.id) {
      return;
    }

    if (!this.googleIsInitialized) {
      this.initializeGoogleIdentity();
      this.renderGoogleButton();
    }

    google.accounts.id.cancel();

    if (this.triggerGoogleRenderedButton()) {
      return;
    }

    // If the hidden GIS button is not ready yet, recreate it and retry.
    this.renderGoogleButton();
    setTimeout(() => {
      this.triggerGoogleRenderedButton();
    }, 0);
  }

  private triggerGoogleRenderedButton(): boolean {
    const container = document.getElementById('googleBtnSeparator');
    if (!container) {
      return false;
    }

    const clickableElement = container.querySelector('[role="button"], div[aria-labelledby]') as HTMLElement;
    if (!clickableElement) {
      return false;
    }

    clickableElement.click();
    return true;
  }

  public handleGoogleLogin(res: any) {
    if (res && res.credential) {
      const idToken = res.credential;
      this.ssoGoogleBackendLogin(idToken);
    }
  }

  private ssoGoogleBackendLogin(token: string) {
    this.signInSubscription = this.userService.ssoGoogleLogin(
      this.userStorageService.getLang(),
      this.userStorageService.getUDID(),
      token
    ).subscribe(
      (res) => {
        if (res) {
          this.tmpContext = res['data']['context'];
          this.companies = res['data']['companies'];
          this.setAccountSelectorData();
          if (this.companies.length === 1) {
            this.selectCompany(this.companies[0].companyCode);
          } else if (this.companies.length > 1) {
            this.router.navigate(['/signin/account-selector']);
          } else {
            this.errorMessage = 'signin-page.snackbar messages.empty companies';
          }
        }
      },
      (error) => {
        this.errorMessage = 'signin-page.snackbar messages.wrong credentials';
      }
    );
  }

  ngOnDestroy() {
    if (this.signInSubscription) {
      this.signInSubscription.unsubscribe();
    }
  }

}
